import { useAuthentication } from '@ecosystem/newron-client-lib';
import { useGetPreferences } from './UseGetPreferences';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DeletePreferenceRequest,
  DeletePreferencesMutateOptions,
  PreferenceResponse,
  PreferenceResponseStatus,
} from '@utils/userPreferencesUtils/Types';
import { PreferenceApiService } from '@services/userPreferences/PreferencesApi.service';

export interface DeletePreferenceMutateResponse<T> {
  data: PreferenceResponse<T> | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  mutate: (variables: DeletePreferenceRequest, options?: DeletePreferencesMutateOptions) => void;
  reset: () => void;
}

/**
 * @return DeletePreferenceMutateResponse Object
 *
 * @remarks
 * Hooks for delete preference api call using useMutate to manage server state
 *
 * @param type       - to difference the different pages (example: "category-leaderboard-group" for Category Leaderboard Page)
 * @param namespace  - combination of retailer client and country
 * **/
export const useDeletePreferences = <T>(type: string, namespace: string): DeletePreferenceMutateResponse<T> => {
  const { getAccessToken } = useAuthentication();
  const getPreferences = useGetPreferences(type, namespace);

  const queryClient = useQueryClient();

  const { data, isPending, isSuccess, isError, mutate, reset } = useMutation({
    mutationKey: ['preferences', type, namespace],

    mutationFn: async (request: DeletePreferenceRequest) => {
      const userToken = await getAccessToken();
      const currentPreference = getPreferences.data.find(
        (preference) => preference.namespace === namespace && preference.key === request.key,
      );

      return PreferenceApiService.deletePreferences(
        userToken ?? '',
        type,
        currentPreference ? currentPreference.id : '',
      );
    },
    onSuccess: async (response) => {
      if (response.status === PreferenceResponseStatus.SUCCESS) {
        await queryClient.invalidateQueries({ queryKey: ['preferences'] });
      }
    },
  });

  return {
    data: data ?? null,
    isLoading: isPending,
    isError,
    isSuccess,
    mutate,
    reset,
  };
};
